import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {
  APIResponse,
  GetConsultationSummaryRequestPayload,
  GetConsultationSummarySuccessResponse,
  GetSpecialistConsultationsRequestPayload,
  GetSpecialistConsultationsSuccessResponse,
  GetSpecialistDashboardStatsSuccessResponse,
  GetPatientDetailsRequestPayload,
  GetPatientDetailsSuccessResponse,
  SpecialistDashboardSliceInitalState,
  UpdateConsultationSummaryRequestPayload,
  UploadConsultationAdditionalFilesSuccessResponse,
  RemoveConsultationAdditionalFilesPayload,
  GetSpecialistListForRefferalSuccessResponse,
  GetSpecialistListForRefferalRequestPayload,
  GetSpecialistPaymentsRequestPayload,
  GetSpecialistPaymentsSuccessResponse,
  RemoveConsultationAdditionalFilesSuccessResponse,
  Consultation,
  SpecialistConsultation,
  ConsultationSummary,
  TranscribeAudioSuccessResponse,
} from '@/interface';
import {ConsultationGroup, ConsultationStatus} from '@/constants';
import {mergeDeepRight} from 'ramda';

const initialState: SpecialistDashboardSliceInitalState = {
  isLoading: false,
  patientDetailsLoading: false,
  consultationAdditionalFilesLoading: false,
  submitSecondOpinionLoading: false,
  specialistDashboardSuccess: false,
  specialistDashboardFailure: false,
  consultation: null,
  patientDetails: null,

  transcribeAudioLoading: false,
  transcribedText: null,

  dashboardState: {
    rating: null,
    total_consultation_case: null,
    total_pending_case: null,
    total_ongoing_count: 0,
    ongoing_consultations: [],
    pending_case: [],
    recent_patients: [],
    recent_feedback: [],
    has_consulted: false,
  },

  consultations: {
    current_tab: ConsultationGroup.Ongoing,
    consultation: [],
    currentPage: 0,
    nextPage: null,
    totalPages: 0,
    totalResults: 0,
  },

  specialist_list: [],

  payments: {
    payments: [],
    currentPage: 1,
    totalPages: 0,
    totalResults: 0,
    nextPage: null,
    isLoading: false,
    errorMessage: undefined,
  },
};

const specialistDashboardSlice = createSlice({
  name: 'specialistDashboard',
  initialState,
  reducers: {
    /**
     * get consultation summary api
     */
    getConsultationSummaryRequest: (state, action: PayloadAction<GetConsultationSummaryRequestPayload>) => {
      state.isLoading = true;
      state.specialistDashboardFailure = false;
      state.specialistDashboardSuccess = false;
    },
    getConsultationSummarySuccess: (state, action: PayloadAction<GetConsultationSummarySuccessResponse>) => {
      state.isLoading = false;
      state.specialistDashboardSuccess = action.payload.status_code;
      state.consultation = action.payload.data;
    },
    getConsultationSummaryFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.specialistDashboardFailure = action.payload.status_code;
    },

    /**
     * get consultation summary api
     */
    specialistDashboardStatsRequest: state => {
      state.isLoading = true;
      state.specialistDashboardFailure = false;
      state.specialistDashboardSuccess = false;
    },
    specialistDashboardStatsSuccess: (state, action: PayloadAction<GetSpecialistDashboardStatsSuccessResponse>) => {
      state.isLoading = false;
      state.specialistDashboardSuccess = action.payload.status_code;
      state.dashboardState = action.payload.data;
    },
    specialistDashboardStatsFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.specialistDashboardFailure = action.payload.status_code;
    },

    /**
     * get specialist consultations api
     */
    getSpecialistConsultationsRequest: (state, action: PayloadAction<GetSpecialistConsultationsRequestPayload>) => {
      state.isLoading = true;
      state.specialistDashboardFailure = false;
      state.specialistDashboardSuccess = false;
    },
    getSpecialistConsultationsSuccess: (state, action: PayloadAction<GetSpecialistConsultationsSuccessResponse>) => {
      state.isLoading = false;
      state.specialistDashboardSuccess = action.payload.status_code;
      if (action.payload.data.current_page === 1) {
        state.consultations.consultation = action.payload.data.consultations;
      } else {
        state.consultations.consultation = [...state.consultations.consultation, ...action.payload.data.consultations];
      }
      state.consultations.currentPage = action.payload.data.current_page;
      state.consultations.nextPage = action.payload.data.next_page;
      state.consultations.totalPages = action.payload.data.total_pages;
      state.consultations.totalResults = action.payload.data.total_results;
    },
    getSpecialistConsultationsFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.specialistDashboardFailure = action.payload.status_code;
    },

    /**
     * get patient details api
     */
    getPatientDetailsRequest: (state, action: PayloadAction<GetPatientDetailsRequestPayload>) => {
      state.patientDetailsLoading = true;
      state.specialistDashboardFailure = false;
      state.specialistDashboardSuccess = false;
    },
    getPatientDetailsSuccess: (state, action: PayloadAction<GetPatientDetailsSuccessResponse>) => {
      state.patientDetailsLoading = false;
      state.specialistDashboardSuccess = action.payload.status_code;
      state.patientDetails = action.payload.data;
    },
    getPatientDetailsFailure: (state, action: PayloadAction<APIResponse>) => {
      state.patientDetailsLoading = false;
      state.specialistDashboardFailure = action.payload.status_code;
    },

    /**
     * updated consultation summary api
     */
    updateConsultationSummaryRequest: (state, action: PayloadAction<UpdateConsultationSummaryRequestPayload>) => {
      state.isLoading = true;
      if (action.payload.submit) {
        state.submitSecondOpinionLoading = true;
      }
      state.specialistDashboardFailure = false;
      state.specialistDashboardSuccess = false;
    },
    updateConsultationSummarySuccess: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.specialistDashboardSuccess = action.payload.status_code;
      state.submitSecondOpinionLoading = false;
    },
    updateConsultationSummaryFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.specialistDashboardFailure = action.payload.status_code;
      state.submitSecondOpinionLoading = false;
    },

    /**
     * upload consultation additional files api
     */
    uploadSpecialistConsultationAdditionalFilesRequest: (state, action: PayloadAction<any>) => {
      state.consultationAdditionalFilesLoading = true;
      state.specialistDashboardFailure = false;
      state.specialistDashboardSuccess = false;
    },
    uploadSpecialistConsultationAdditionalFilesSuccess: (state, action: PayloadAction<UploadConsultationAdditionalFilesSuccessResponse>) => {
      state.consultationAdditionalFilesLoading = false;
      state.specialistDashboardSuccess = action.payload.status_code;
      if (state.consultation && action.payload.data.id === state.consultation.id) {
        state.consultation.additional_files = [...state.consultation.additional_files, ...action.payload.data.files];
      }
    },
    uploadSpecialistConsultationAdditionalFilesFailure: (state, action: PayloadAction<APIResponse>) => {
      state.consultationAdditionalFilesLoading = false;
      state.specialistDashboardFailure = action.payload.status_code;
    },

    removeSpecialistConsultationAdditionalFilesRequest: (state, action: PayloadAction<RemoveConsultationAdditionalFilesPayload>) => {
      state.consultationAdditionalFilesLoading = true;
      state.specialistDashboardFailure = false;
      state.specialistDashboardSuccess = false;
    },
    removeSpecialistConsultationAdditionalFilesSuccess: (state, action: PayloadAction<RemoveConsultationAdditionalFilesSuccessResponse>) => {
      state.consultationAdditionalFilesLoading = false;
      state.specialistDashboardSuccess = action.payload.status_code;
      if (state.consultation && action.payload.data.id === state.consultation.id) {
        state.consultation.additional_files = state.consultation.additional_files.filter(file => file.id !== action.payload.data.file_id);
      }
    },
    removeSpecialistConsultationAdditionalFilesFailure: (state, action: PayloadAction<APIResponse>) => {
      state.consultationAdditionalFilesLoading = false;
      state.specialistDashboardFailure = action.payload.status_code;
    },

    /**
     * get specialist list for refferal api
     */
    getSpecialistListForRefferalRequest: (state, action: PayloadAction<GetSpecialistListForRefferalRequestPayload>) => {
      state.isLoading = true;
      state.specialistDashboardFailure = false;
      state.specialistDashboardSuccess = false;
    },
    getSpecialistListForRefferalSuccess: (state, action: PayloadAction<GetSpecialistListForRefferalSuccessResponse>) => {
      state.isLoading = false;
      state.specialistDashboardSuccess = action.payload.status_code;
      state.specialist_list = action.payload.data;
    },
    getSpecialistListForRefferalFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.specialistDashboardFailure = action.payload.status_code;
    },

    /**
     * get specialist payments api
     */
    getSpecialistPaymentsRequest: (state, action: PayloadAction<GetSpecialistPaymentsRequestPayload>) => {
      state.payments.isLoading = true;
      state.payments.errorMessage = undefined;
      state.specialistDashboardFailure = false;
      state.specialistDashboardSuccess = false;
    },
    getSpecialistPaymentsSuccess: (state, action: PayloadAction<GetSpecialistPaymentsSuccessResponse>) => {
      state.payments.isLoading = false;
      state.specialistDashboardSuccess = action.payload.status_code;
      const responseData = action.payload.data;
      state.payments.payments = responseData.payments || [];
      state.payments.currentPage = responseData.current_page || 1;
      state.payments.totalPages = responseData.total_pages || 0;
      state.payments.nextPage = responseData.next_page || null;
      const estimatedTotal =
        responseData.total_pages > 0
          ? responseData.current_page === responseData.total_pages
            ? (responseData.total_pages - 1) * 50 + (responseData.payments?.length || 0)
            : responseData.total_pages * 50
          : responseData.payments?.length || 0;
      state.payments.totalResults = estimatedTotal;
    },
    getSpecialistPaymentsFailure: (state, action: PayloadAction<APIResponse>) => {
      state.payments.isLoading = false;
      state.payments.payments = [];
      state.specialistDashboardFailure = action.payload.status_code;
      if (action.payload.status_code === 'E-10044' || action.payload.status_code === 'E-10054') {
        state.payments.errorMessage = 'No payments found';
      } else {
        state.payments.errorMessage = 'Unable to fetch payments';
      }
    },

    /**
     * remove license api
     */
    removeLicenseRequest: state => {
      state.isLoading = true;
      state.specialistDashboardFailure = false;
      state.specialistDashboardSuccess = false;
    },
    removeLicenseSuccess: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.specialistDashboardSuccess = action.payload.status_code;
    },
    removeLicenseFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.specialistDashboardFailure = action.payload.status_code;
    },

    /**
     * remove signature api
     */
    removeSignatureRequest: state => {
      state.isLoading = true;
      state.specialistDashboardFailure = false;
      state.specialistDashboardSuccess = false;
    },
    removeSignatureSuccess: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.specialistDashboardSuccess = action.payload.status_code;
    },
    removeSignatureFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.specialistDashboardFailure = action.payload.status_code;
    },

    /**
     * utility reducers
     */
    resetSpecialistDashboardMessage: state => {
      state.specialistDashboardFailure = false;
      state.specialistDashboardSuccess = false;
    },

    cancelSpecialistDashboardRequest: state => {
      state.isLoading = false;
      state.patientDetailsLoading = false;
      state.submitSecondOpinionLoading = false;
      state.transcribeAudioLoading = false;
      },

      /**
       * Transcribe Audio (Speech-to-Text) API's
       */
      transcribeAudioRequest: (state, _action: PayloadAction<FormData>) => {
        state.transcribeAudioLoading = true;
        state.specialistDashboardFailure = false;
        state.specialistDashboardSuccess = false;
        state.transcribedText = null;
      },
      transcribeAudioSuccess: (state, action: PayloadAction<TranscribeAudioSuccessResponse>) => {
        state.transcribeAudioLoading = false;
        state.specialistDashboardSuccess = action.payload.status_code;
        state.transcribedText = action.payload.data?.text || null;
      },
      transcribeAudioFailure: (state, action: PayloadAction<APIResponse>) => {
        state.transcribeAudioLoading = false;
        state.specialistDashboardFailure = action.payload.status_code;
        state.transcribedText = null;
      },
      clearTranscribedText: (state) => {
        state.transcribedText = null;
      },

    setSpecialistCurrentConsultationTab: (state, action: PayloadAction<ConsultationGroup>) => {
      state.consultations.current_tab = action.payload;
    },

    updateSpecialistConsultationData(state, action: PayloadAction<{id: number; data: Partial<Omit<SpecialistConsultation, 'id'>>}>) {
      state.consultations.consultation = state.consultations.consultation
        .map(consultation => {
          // update the consultation in the same tab
          if (consultation.id === action.payload.id) {
            // on ongoing tab, if the status is report ready, then remove the consultation from the list
            if (action.payload.data?.status?.id === ConsultationStatus.CancelledByPatient && state.consultations.current_tab === ConsultationGroup.Ongoing) {
              return null;
            }

            // on past tab, if the status is completed (completed as follow or referral was made), then remove the consultation from the list
            if (action.payload.data?.status?.id === ConsultationStatus.Completed && state.consultations.current_tab === ConsultationGroup.Past) {
              return null;
            }
            return {...consultation, ...action.payload.data};
          }
          return consultation;
        })
        .filter(consultation => consultation !== null);
    },

    updateSpecialistConsulationSummaryData(state, action: PayloadAction<{id: number; data: Partial<ConsultationSummary>}>) {
      if (state.consultation && state.consultation.id === action.payload.id) {
        const new_payload = mergeDeepRight(state.consultation, action.payload.data) as ConsultationSummary;
        state.consultation = new_payload;
      }
    },

    addSpecialistConsultationData(state, action: PayloadAction<SpecialistConsultation>) {
      // do not add if consultation already exists
      const alreadyExists = state.consultations.consultation.some(c => c.id === action.payload.id);
      if (alreadyExists) return;

      // add the consultation to the dashboard list (keep recent 2 only)
      const next = [
        {
          id: action.payload.id,
          created_at: action.payload.created_at,
          deadline_at: action.payload.deadline_at,
          patient_name: action.payload.patient_name,
          type: action.payload.type.id,
          status: action.payload.status.id,
        },
        ...state.dashboardState.ongoing_consultations,
      ];
      state.dashboardState.ongoing_consultations = next.slice(0, 2);
      state.dashboardState.total_ongoing_count = (state.dashboardState.total_ongoing_count ?? 0) + 1;

      // add the consultation to the ongoing consultations list
      if (state.consultations.current_tab === ConsultationGroup.Ongoing) {
        state.consultations.consultation = [action.payload, ...state.consultations.consultation];
      }
    },
  },
});

export const {
  // get consultation summary api
  getConsultationSummaryRequest,
  getConsultationSummarySuccess,
  getConsultationSummaryFailure,

  // get specialist dashboard stats api
  specialistDashboardStatsRequest,
  specialistDashboardStatsSuccess,
  specialistDashboardStatsFailure,

  // get specialist consultations api
  getSpecialistConsultationsRequest,
  getSpecialistConsultationsSuccess,
  getSpecialistConsultationsFailure,

  // get patient details api
  getPatientDetailsRequest,
  getPatientDetailsSuccess,
  getPatientDetailsFailure,

  // updated consultation summary api
  updateConsultationSummaryRequest,
  updateConsultationSummarySuccess,
  updateConsultationSummaryFailure,

  // upload specialist consultation additional documents api
  uploadSpecialistConsultationAdditionalFilesRequest,
  uploadSpecialistConsultationAdditionalFilesSuccess,
  uploadSpecialistConsultationAdditionalFilesFailure,

  // remove documents api
  removeSpecialistConsultationAdditionalFilesFailure,
  removeSpecialistConsultationAdditionalFilesRequest,
  removeSpecialistConsultationAdditionalFilesSuccess,

  // get specialist list for refferal api
  getSpecialistListForRefferalRequest,
  getSpecialistListForRefferalSuccess,
  getSpecialistListForRefferalFailure,

  // get specialist payments api
  getSpecialistPaymentsRequest,
  getSpecialistPaymentsSuccess,
  getSpecialistPaymentsFailure,

  // remove license api
  removeLicenseRequest,
  removeLicenseSuccess,
  removeLicenseFailure,

  // remove signature api
  removeSignatureRequest,
  removeSignatureSuccess,
  removeSignatureFailure,

  // cancel and reset reducers
  resetSpecialistDashboardMessage,
  cancelSpecialistDashboardRequest,

  // consultation search
  setSpecialistCurrentConsultationTab,
  updateSpecialistConsultationData,
  addSpecialistConsultationData,
  updateSpecialistConsulationSummaryData,

  // Speech to Text
  transcribeAudioRequest,
  transcribeAudioSuccess,
  transcribeAudioFailure,
  clearTranscribedText,
} = specialistDashboardSlice.actions;

export default specialistDashboardSlice.reducer;
